/**
 * accountLanguage.js — Langue de l'interface et langue du compte (D251).
 *
 * Le compte porte une langue (User.language) qui choisit la langue des
 * courriels. L'interface la suit sur un navigateur sans choix local, et tout
 * choix fait sur ce navigateur (sélecteur, profil) est renvoyé au compte.
 *
 * « Choix local » : la clé `ekynair_lang` présente AVANT l'initialisation
 * d'i18next, ou un choix fait pendant la session. Le détecteur écrit la clé
 * dès le premier chargement (caches: ['localStorage']) : lue après
 * l'initialisation, elle serait toujours présente.
 */
export const LANGUAGE_STORAGE_KEY = 'ekynair_lang';
export const SUPPORTED_LANGUAGES = ['fr', 'en'];

let localChoice = false;
// Dernière langue connue du compte connecté : évite de renvoyer au serveur la
// langue qu'il vient de donner (adoption à la connexion, profil enregistré).
let knownAccountLanguage = null;

/** @returns {'fr'|'en'|null} */
export function normalizeLanguage(value) {
  return typeof value === 'string' && SUPPORTED_LANGUAGES.includes(value) ? value : null;
}

/** Langue affichée par l'interface (code court, français par défaut). */
export function currentLanguage(i18n) {
  return normalizeLanguage((i18n.resolvedLanguage || i18n.language || '').slice(0, 2)) || 'fr';
}

/** À appeler avant i18n.init : mémorise si ce navigateur avait déjà un choix. */
export function captureLocalLanguageChoice() {
  try {
    localChoice = window.localStorage.getItem(LANGUAGE_STORAGE_KEY) !== null;
  } catch {
    // Stockage inaccessible (navigation privée stricte) : aucun choix local
    localChoice = false;
  }
}

export const hasLocalLanguageChoice = () => localChoice;
export const markLocalLanguageChoice = () => { localChoice = true; };

export const getKnownAccountLanguage = () => knownAccountLanguage;
export const setKnownAccountLanguage = (language) => { knownAccountLanguage = normalizeLanguage(language); };

/**
 * Langue choisie au profil et enregistrée : elle devient aussi celle de
 * l'interface, comme un choix fait au sélecteur.
 */
export function applySavedAccountLanguage(i18n, language) {
  const saved = normalizeLanguage(language);
  if (!saved) return;
  setKnownAccountLanguage(saved);
  markLocalLanguageChoice();
  if (currentLanguage(i18n) !== saved) i18n.changeLanguage(saved);
}
